<template>
	<footer
		class="nav-footer mt-auto"
		:class="{ 'nav-footer--mobile': isMobile, '-mt-4': lostFolders && isOwner }"
	>
		<CFolderUpsell v-if="lostFolders && isOwner" :org-code="orgCode" />
		<div class="flex flex-wrap justify-between items-end py-3 border-t" :class="{ 'border-primary': hasScroll || isMobile, 'border-transparent': !hasScroll && !isMobile }">
			<CNewButtonMenu
				v-if="isAdmin || canOnlyCreateForms"
				:org-code="orgCode"
				:can-create-folders="canCreateFolders"
				:can-invite-users="canInviteUsers"
				:can-only-create-forms="canOnlyCreateForms"
				:client-role="clientRole"
				@handling-interaction="$emit('handling-interaction', $event)"
				@create-folder="$emit('create-folder')"
			/>
			<slot>
				<CToolTip
					v-if="!isPopperOnlyState || isModal"
					:use-button="false"
					placement="top"
					popper-class="tooltip--nav tooltip--nav-shortcut"
					class="ml-auto"
				>
					<CShortcutKeys />
					<template #button>
						<CIconButton
							class="button--no-style text-icon-subtle ml-auto"
							:class="{'rotate-180': isPopper && !isModal}"
							:intense="false"
							aria-controls="side-menu"
							:aria-label="resources.CloseSidebar"
							icon="collapse-menu"
							@click="handleToggle"
						/>
					</template>
				</CToolTip>
			</slot>
		</div>
	</footer>
</template>

<script lang="ts">
	import CIconButton from 'src/components/interaction/IconButton.vue';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import CFolderUpsell from './FolderUpsell.vue';
	import CNewButtonMenu from './NewButtonMenu.vue';
	import type { PropType } from 'vue';
	import { inject, computed } from 'vue';
	import { isMobileKey, localizationResourcesKey } from 'src/utilities/injection-keys';
	import { useRoute } from 'vue-router/composables';
	import CShortcutKeys from 'src/components/info/ShortcutKeys.vue';
	import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';

	export default {
		name: 'CNavFooter',
		components: {
			CIconButton,
			CToolTip,
			CFolderUpsell,
			CNewButtonMenu,
			CShortcutKeys
		},
		props: {
			isPopper: {
				type: Boolean,
				default: false
			},
			isModal: {
				type: Boolean,
				default: false
			},
			hasScroll: {
				type: Boolean,
				default: false
			},
			orgCode: {
				type: String,
				required: true
			},
			clientRole: {
				type: String as PropType<ClientRole>,
				default: null
			},
			canCreateFolders: {
				type: Boolean,
				required: true
			},
			lostFolders: {
				type: Boolean,
				required: true
			},
			canInviteUsers: {
				type: Boolean,
				required: true
			},
			canOnlyCreateForms: {
				type: Boolean,
				required: true
			}
		},
		emits: ['handling-interaction', 'create-folder', 'toggle-open', 'close-popper'],
		setup(props, { emit }) {
			const isMobile = inject(isMobileKey);
			const resources = inject(localizationResourcesKey);
			const route = useRoute();

			const isOwner = computed(() => props.clientRole === ClientRole.Owner);
			const isAdmin = computed(() => isOwner.value || props.clientRole === ClientRole.Administrator);

			const handleToggle = () => {
				if (props.isModal)
					emit('close-popper');
				else
					emit('toggle-open');
			};

			return {
				isMobile,
				resources,
				isPopperOnlyState: computed(() => route.meta.showNavAsPopperOnly),
				handleToggle,
				isOwner,
				isAdmin
			};
		}
	};
</script>

<style lang="scss">
.nav-footer {
	position: sticky;
	bottom: 0;
	width: 100%;
	border-color: transparent;
	transition: border-color .2s;

	:root & > div {
		padding-right: calc($space__4 - var(--space__touch-target));
		padding-left: calc($space__4 + env(safe-area-inset-left, 0px));
	}
}
</style>
